import { Select, MenuItem, FormControl, InputLabel, Theme, useTheme } from '@mui/material';
import { SelectChangeEvent } from '@mui/material';

interface ContactFormSelectOptions {
  required?: boolean;
  variant?: 'standard' | 'outlined';
  error?: boolean;
}

interface ContactFormSelectProps {
  id: string;
  label: string;
  value: string;
  handleInputChange: (value: string) => void;
  choices: Array<string>;
  options?: ContactFormSelectOptions;
}

/**
 * Styled select dropdown to use on contact us form.
 * @param {string} id ID of select on form.
 * @param {string} label Label of select to display on form.
 * @param {string} value Controlled value of select from parent.
 * @param {void} handleInputChange Update value of select field in parent.
 * @param {Array<string>} choices List of menu item choices for select dropdown.
 * @param {ContactFormSelectOptions | undefined} options Optional additional customization to give to select.
 * @returns {JSX.Element}
 */
const ContactFormSelect = ({
  id,
  label,
  value,
  choices,
  handleInputChange,
  options
}: ContactFormSelectProps) => {
  const theme: Theme = useTheme();
  return (
    <FormControl
      sx={{
        '& .MuiFormLabel-root': {
          color: theme.palette.secondary.dark
        },
        '& .MuiFormLabel-root.Mui-focused': {
          color: theme.palette.secondary.dark
        },
        maxWidth: '100%'
      }}
      fullWidth
      className="select-form-control"
      variant={options?.variant}
    >
      <InputLabel id={id}>{`${label}${options?.required ? ' *' : ''}`}</InputLabel>
      <Select
        defaultValue=""
        value={value}
        error={options?.error}
        required={options?.required}
        onChange={(event: SelectChangeEvent) => handleInputChange(event.target.value)}
        role="listbox"
        aria-label="select"
        sx={{
          '& .MuiSelect-select': {
            color: theme.palette.secondary.dark
          },
          '& .MuiSelect-icon': {
            color: theme.palette.secondary.dark
          },
          '& .MuiInputBase-input': {
            color: theme.palette.primary.main,
            textAlign: 'start'
          }
        }}
      >
        {choices.map((choice: string) => {
          return (
            <MenuItem
              key={choice}
              value={choice}
              sx={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                mx: 1
              }}
            >
              {choice}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default ContactFormSelect;
